"use client";

import posthog from "posthog-js";

export type GrowthAnalyticsEvent =
  | "sqlite_demo_loaded"
  | "sqlite_demo_query_started"
  | "sqlite_demo_query_completed"
  | "sqlite_demo_query_failed"
  | "sqlite_demo_signup_clicked"
  | "signup_started"
  | "signup_succeeded"
  | "signup_failed"
  | "signup_validation_failed"
  | "onboarding_step_viewed"
  | "onboarding_step_completed"
  | "onboarding_plan_selected"
  | "onboarding_email_verification_failed"
  | "onboarding_verification_email_changed"
  | "onboarding_subscription_succeeded"
  | "onboarding_subscription_failed"
  | "onboarding_completed"
  | "project_created"
  | "project_creation_failed"
  | "table_created"
  | "first_file_uploaded"
  | "first_extraction_started"
  | "first_extraction_completed"
  | "contact_form_started"
  | "contact_form_submitted"
  | "contact_form_failed";

export function captureGrowthEvent(
  event: GrowthAnalyticsEvent,
  properties?: Record<string, unknown>,
) {
  posthog.capture(event, properties);
}

const SIGNUP_METHOD_KEY = "pdfparse.signup_method";

export function markSignupStarted(method: "email" | "google" | "github") {
  window.sessionStorage.setItem(SIGNUP_METHOD_KEY, method);
}

export function consumeSignupMethod() {
  const method = window.sessionStorage.getItem(SIGNUP_METHOD_KEY);
  if (method) window.sessionStorage.removeItem(SIGNUP_METHOD_KEY);
  return method;
}

function captureFirstUserEvent(
  event:
    | "first_file_uploaded"
    | "first_extraction_started"
    | "first_extraction_completed"
    | "onboarding_subscription_succeeded"
    | "onboarding_completed",
  userId: string,
  properties?: Record<string, unknown>,
) {
  const storageKey = `pdfparse.${event}.${userId}`;
  try {
    if (window.localStorage.getItem(storageKey)) return;
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }

  captureGrowthEvent(event, properties);
  try {
    window.localStorage.setItem(storageKey, "true");
  } catch {
    // Analytics must never interrupt the product flow.
  }
}

export function captureFirstFileUploaded(
  userId: string,
  properties?: Record<string, unknown>,
) {
  captureFirstUserEvent("first_file_uploaded", userId, properties);
}

export function captureFirstExtractionStarted(
  userId: string,
  properties?: Record<string, unknown>,
) {
  captureFirstUserEvent("first_extraction_started", userId, properties);
}

export function captureOnboardingCompleted(
  userId: string,
  properties?: Record<string, unknown>,
) {
  captureFirstUserEvent("onboarding_completed", userId, properties);
}

export function captureOnboardingSubscriptionSucceeded(
  userId: string,
  properties?: Record<string, unknown>,
) {
  captureFirstUserEvent(
    "onboarding_subscription_succeeded",
    userId,
    properties,
  );
}

export function captureFirstExtractionCompleted(
  userId: string,
  properties?: Record<string, unknown>,
) {
  captureFirstUserEvent("first_extraction_completed", userId, properties);
}
